import React, { useEffect, useState } from "react";
import AdminLayout from "../components/AdminLayout";
import StatusChip from "../components/StatusChip";
import PackageUsageDrill from "../components/PackageUsageDrill";
import LogSessionDialog from "../components/LogSessionDialog";
import api, { formatDate, formatTime, formatPrice, formatApiError } from "../lib/api";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ChevronRight, Plus } from "lucide-react";

const PKG_STATUS_STYLES = {
  active: "bg-status-success-bg text-status-success",
  low: "bg-status-warning-bg text-status-warning",
  depleted: "bg-slate/15 text-slate",
  expired: "bg-status-danger-bg/50 text-status-danger",
};

export default function ClientDetail() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [tab, setTab] = useState("visits");
  const [notes, setNotes] = useState("");
  const [packageTypes, setPackageTypes] = useState([]);
  const [showSell, setShowSell] = useState(false);
  const [sellType, setSellType] = useState("");
  const [sellMethod, setSellMethod] = useState("external");
  const [drillPackageId, setDrillPackageId] = useState(null);
  const [drillError, setDrillError] = useState("");
  const [showLogSession, setShowLogSession] = useState(false);
  const [error, setError] = useState("");

  const load = () => api.get(`/clients/${id}`).then(({ data }) => { setData(data); setNotes(data.client.operational_notes || ""); });

  useEffect(() => {
    load();
    api.get("/package-types").then(({ data }) => setPackageTypes(data));
  }, [id]);

  if (!data) {
    return <AdminLayout><div className="text-sm font-ui text-slate">Loading…</div></AdminLayout>;
  }
  const c = data.client;

  const saveNotes = async () => {
    await api.put(`/clients/${id}/notes`, { notes });
    load();
  };

  const sellPackage = async () => {
    setError("");
    try {
      await api.post("/packages/sell", {
        client_id: id,
        package_type_id: sellType,
        payment_method: sellMethod,
      });
      setShowSell(false);
      setSellType("");
      load();
    } catch (e) {
      setError(formatApiError(e));
    }
  };

  return (
    <AdminLayout>
      <Link to="/admin/clients" className="inline-flex items-center gap-1 text-sm font-ui text-slate hover:text-forest mb-4" data-testid="back-to-clients">
        <ArrowLeft className="w-4 h-4" /> All clients
      </Link>

      {/* Header */}
      <div className="bg-white border border-hairline rounded-lg shadow-warm p-6 mb-6">
        <div className="flex items-start justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-pill bg-cream flex items-center justify-center text-forest font-medium text-lg">
              {c.first_name?.[0]}{c.last_initial?.[0]}
            </div>
            <div>
              <h1 className="font-display text-3xl text-charcoal">{c.first_name} {c.last_initial}.</h1>
              <p className="font-mono text-xs text-slate mt-1" data-testid="client-id">client_id: {c.id.slice(0, 8)}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <span className={`px-2.5 py-1 rounded-pill text-xs font-ui ${c.waiver_status === "signed" ? "bg-status-success-bg text-status-success" : "bg-status-warning-bg text-status-warning"}`}>
              waiver: {c.waiver_status}
            </span>
          </div>
        </div>
        <div className="grid sm:grid-cols-3 gap-4 mt-6 pt-6 border-t border-hairline">
          <div><p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">Email</p><p className="font-ui text-sm text-charcoal mt-1">{c.email}</p></div>
          <div><p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">Phone</p><p className="font-ui text-sm text-charcoal mt-1">{c.phone}</p></div>
          <div><p className="font-mono text-[10px] uppercase tracking-[0.18em] text-slate">Preferred channel</p><p className="font-ui text-sm text-charcoal mt-1">{c.preferred_channel}</p></div>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-6 border-b border-hairline mb-6">
        {["visits","packages","tinctures","waivers","notes"].map((t) => (
          <button key={t} onClick={() => setTab(t)} className={`pb-3 -mb-px text-sm font-ui capitalize ${tab === t ? "text-forest border-b-2 border-sage" : "text-slate hover:text-forest"}`} data-testid={`tab-${t}`}>{t}</button>
        ))}
      </div>

      {tab === "visits" && (
        <div>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm font-ui text-slate">{data.appointments.length} visit{data.appointments.length === 1 ? "" : "s"} on file</p>
            <button onClick={() => setShowLogSession(true)} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md inline-flex items-center gap-1.5" data-testid="open-log-session">
              <Plus className="w-4 h-4" /> Log a session
            </button>
          </div>
          <div className="bg-white border border-hairline rounded-lg shadow-warm divide-y divide-hairline" data-testid="visits-list">
            {data.appointments.length === 0 && <div className="px-6 py-12 text-center text-sm font-ui text-slate">No visits yet. Click <strong className="text-forest">Log a session</strong> to add one.</div>}
            {data.appointments.map((a) => (
              <div key={a.id} className="px-6 py-4 flex items-center justify-between gap-4" data-testid={`visit-row-${a.id}`}>
                <div>
                  <p className="font-ui text-sm text-charcoal">{a.service?.name} <span className="text-slate">with</span> {a.practitioner?.display_name}</p>
                  <p className="text-xs font-ui text-slate">{formatDate(a.scheduled_start)} · {formatTime(a.scheduled_start)}{a.outcome_notes ? ` · ${a.outcome_notes}` : ""}</p>
                </div>
                <StatusChip status={a.status} />
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === "packages" && (
        <div>
          <div className="flex justify-end mb-4">
            <button onClick={() => setShowSell(!showSell)} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md" data-testid="sell-package-toggle">
              {showSell ? "Cancel" : "Sell a package"}
            </button>
          </div>
          {showSell && (
            <div className="bg-white border border-hairline rounded-lg p-5 mb-4">
              {error && <div className="mb-3 text-status-danger text-sm font-ui">{error}</div>}
              <div className="grid sm:grid-cols-3 gap-3">
                <select value={sellType} onChange={(e) => setSellType(e.target.value)} className="px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui" data-testid="sell-package-type">
                  <option value="">Pick a package…</option>
                  {packageTypes.map((pt) => <option key={pt.id} value={pt.id}>{pt.name} — {formatPrice(pt.price)}</option>)}
                </select>
                <select value={sellMethod} onChange={(e) => setSellMethod(e.target.value)} className="px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui" data-testid="sell-payment-method">
                  <option value="external">External</option>
                  <option value="cash">Cash</option>
                  <option value="card">Card (Square)</option>
                </select>
                <button onClick={sellPackage} disabled={!sellType} className="px-4 py-2 bg-forest hover:bg-charcoal disabled:opacity-40 text-white text-sm font-ui rounded-md" data-testid="sell-package-confirm">Record sale</button>
              </div>
            </div>
          )}
          <div className="bg-white border border-hairline rounded-lg shadow-warm divide-y divide-hairline" data-testid="packages-list">
            {data.packages.length === 0 && <div className="px-6 py-12 text-center text-sm font-ui text-slate">No packages on file.</div>}
            {data.packages.map((p) => {
              const remaining = p.remaining ?? (p.original_quantity - p.used_quantity);
              const ratio = remaining / Math.max(p.original_quantity, 1);
              const dotClass = ratio > 0.5 ? "bg-sage" : ratio > 0.2 ? "bg-status-warning" : "bg-status-danger";
              const status = p.status || (remaining <= 0 ? "depleted" : remaining <= 2 ? "low" : "active");
              const statusCls = PKG_STATUS_STYLES[status] || "bg-slate/15 text-slate";
              const open = drillPackageId === p.id;
              return (
                <div key={p.id} data-testid={`client-package-row-${p.id}`}>
                  <button
                    onClick={() => setDrillPackageId(open ? null : p.id)}
                    className={`w-full text-left px-6 py-4 flex items-center justify-between gap-3 hover:bg-cream/30 transition ${open ? "bg-cream/40" : ""}`}
                    title="Click to see this package's usage history"
                    data-testid={`client-package-toggle-${p.id}`}
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <ChevronRight className={`w-3.5 h-3.5 text-slate transition-transform shrink-0 ${open ? "rotate-90" : ""}`} />
                      <div className="min-w-0">
                        <p className="font-ui text-sm text-charcoal truncate">{p.package_type?.name || "—"}</p>
                        <p className="text-xs font-ui text-slate">
                          Purchased {formatDate(p.purchased_at)}
                          {p.expires_at && <> · Expires {formatDate(p.expires_at)}</>}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className={`px-2 py-0.5 rounded-pill text-[10px] font-mono uppercase tracking-wider ${statusCls}`}>{status}</span>
                      <span className={`w-2.5 h-2.5 rounded-pill ${dotClass}`} />
                      <span className="font-mono text-sm text-charcoal">{remaining} / {p.original_quantity}</span>
                    </div>
                  </button>
                  {open && (
                    <PackageUsageDrill
                      packageId={p.id}
                      onReverse={load}
                      onError={setDrillError}
                    />
                  )}
                  {open && drillError && (
                    <div className="px-6 py-2 bg-status-danger-bg/30 text-xs font-ui text-status-danger" data-testid="client-package-drill-error">{drillError}</div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}

      {tab === "tinctures" && (
        <div className="bg-white border border-hairline rounded-lg shadow-warm divide-y divide-hairline" data-testid="tinctures-list">
          {data.tinctures.length === 0 && <div className="px-6 py-12 text-center text-sm font-ui text-slate">No tinctures logged yet.</div>}
          {data.tinctures.map((t) => (
            <div key={t.id} className="px-6 py-4">
              <div className="flex items-start justify-between">
                <div>
                  <p className="font-ui text-sm text-charcoal">{t.product_name}</p>
                  <p className="text-xs font-ui text-slate">{t.quantity} · logged {formatDate(t.created_at)}</p>
                </div>
                {t.estimated_refill_at && (
                  <span className="text-xs font-ui text-status-info bg-status-info-bg px-2 py-1 rounded-pill">
                    refill ~ {formatDate(t.estimated_refill_at)}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === "waivers" && (
        <div className="bg-white border border-hairline rounded-lg shadow-warm divide-y divide-hairline" data-testid="waivers-list">
          {data.waivers.length === 0 && <div className="px-6 py-12 text-center text-sm font-ui text-slate">No signed waivers yet.</div>}
          {data.waivers.map((w) => (
            <div key={w.id} className="px-6 py-4 flex items-center justify-between">
              <div>
                <p className="font-ui text-sm text-charcoal">Signed by: {w.signature_text}</p>
                <p className="text-xs font-ui text-slate">{formatDate(w.signed_at)} · expires {formatDate(w.expires_at)}</p>
              </div>
              <span className="font-mono text-xs text-slate">{w.id.slice(0, 8)}</span>
            </div>
          ))}
        </div>
      )}

      {tab === "notes" && (
        <div className="bg-white border border-hairline rounded-lg shadow-warm p-6">
          <p className="text-xs font-ui text-slate mb-3">Operational notes (intentionally not medical history).</p>
          <textarea
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            rows={6}
            className="w-full px-3 py-2 bg-white border border-hairline rounded-md text-sm font-ui text-charcoal focus:outline-none focus:border-sage focus:ring-2 focus:ring-sage/30"
            data-testid="notes-textarea"
          />
          <div className="flex justify-end mt-3">
            <button onClick={saveNotes} className="px-4 py-2 bg-forest hover:bg-charcoal text-white text-sm font-ui rounded-md" data-testid="save-notes">Save notes</button>
          </div>
        </div>
      )}

      {showLogSession && (
        <LogSessionDialog
          clientId={id}
          packages={data.packages || []}
          onClose={() => setShowLogSession(false)}
          onSaved={async () => { setShowLogSession(false); await load(); }}
        />
      )}
    </AdminLayout>
  );
}
